import { Node } from '@tiptap/pm/model';
import { Extension } from '@tiptap/core';
import { Transaction } from '@tiptap/pm/state';
import { EditorState } from '@tiptap/pm/state';

interface IndentOptions {
  types: string[];
  minLevel: number;
  maxLevel: number;
  indentStep: number;
}

export const IndentExtension = Extension.create<IndentOptions>({
  name: 'indent',

  addOptions() {
    return {
      minLevel: 0,
      maxLevel: 8,
      indentStep: 24,
      types: ['paragraph', 'heading'],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element) => {
              const marginLeft = element.style.marginLeft;
              const level = marginLeft
                ? parseInt(marginLeft, 10) / this.options.indentStep
                : 0;
              return level > this.options.minLevel ? level : null;
            },
            renderHTML: (attributes) => {
              if (
                !attributes.indent ||
                attributes.indent <= this.options.minLevel
              ) {
                return {};
              }
              return {
                style: `margin-left: ${attributes.indent * this.options.indentStep}px`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indent:
        () =>
        ({
          tr,
          state,
          dispatch,
        }: {
          tr: Transaction;
          state: EditorState;
          dispatch?: (tr: Transaction) => void;
        }) => {
          const { selection } = state;
          const { to, from } = selection;

          state.doc.nodesBetween(from, to, (node: Node, pos: number) => {
            if (this.options.types.includes(node.type.name)) {
              const currentIndent = node.attrs.indent || 0;
              const newIndent = Math.max(
                this.options.minLevel,
                Math.min(this.options.maxLevel, currentIndent + 1)
              );

              if (newIndent !== currentIndent) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  indent: newIndent,
                });
              }
            }
          });

          if (dispatch) dispatch(tr);
          return true;
        },
      outdent:
        () =>
        ({
          tr,
          state,
          dispatch,
        }: {
          tr: Transaction;
          state: EditorState;
          dispatch?: (tr: Transaction) => void;
        }) => {
          const { selection } = state;
          const { to, from } = selection;

          state.doc.nodesBetween(from, to, (node: Node, pos: number) => {
            if (this.options.types.includes(node.type.name)) {
              const currentIndent = node.attrs.indent || 0;
              const newIndent = Math.max(
                this.options.minLevel,
                Math.min(this.options.maxLevel, currentIndent - 1)
              );

              if (newIndent !== currentIndent) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  indent: newIndent,
                });
              }
            }
          });

          if (dispatch) dispatch(tr);
          return true;
        },
    };
  },
});
